"use client";

/**
 * "Notify me about listings like this" on /elan, in one tap and without
 * an account: this browser's push subscription carries one saved search
 * (POST /alerts/anon, the same device path as the alert form and the
 * residence follow). The search is prefilled from the listing's facets:
 * its residence or place, rooms, deal and a price band around it.
 *
 * When push cannot carry it (iOS Safari tab, permission refused, push
 * off on the server, the one-per-device search already used) the tap
 * opens the alert form with the same search filled in, so it never
 * dead-ends. After a save the account is offered, never forced.
 */
import { useEffect, useState } from "react";
import { LINK } from "@/lib/ui-classes";
import { Bell, Check, Loader2 } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { encodePrefill } from "@/lib/filter-presets";
import { getOrCreatePushSubscription, isPushEligible } from "@/lib/push";
import { useAuth } from "@/lib/auth-context";
import { trackEvent } from "@/lib/tracking";

type Loc = "az" | "ru" | "en";

const COPY: Record<Loc, { cta: string; sub: string; subIn: string; subForm: string; done: string; account: string; mine: string }> = {
  az: {
    cta: "Oxşar elan çıxanda xəbər verin",
    sub: "Hesabsız, bir toxunuşla",
    subIn: "Bir toxunuşla, bildirişlərinizə əlavə olunur",
    subForm: "Axtarış artıq doldurulub",
    done: "Hazırdır. Oxşar elan çıxanda bu brauzerə bildiriş göndərəcəyik.",
    account: "Hesab yaradın, bildirişləri idarə edin",
    mine: "Bildirişlərimə baxın",
  },
  ru: {
    cta: "Сообщить о похожих объявлениях",
    sub: "Без регистрации, в одно нажатие",
    subIn: "В одно нажатие, появится в ваших уведомлениях",
    subForm: "Поиск уже заполнен",
    done: "Готово. Когда появится похожее объявление, пришлём уведомление в этот браузер.",
    account: "Создайте аккаунт, чтобы управлять уведомлениями",
    mine: "Мои уведомления",
  },
  en: {
    cta: "Notify me about listings like this",
    sub: "No account needed, one tap",
    subIn: "One tap, added to your alerts",
    subForm: "The search comes filled in",
    done: "Done. We will notify this browser when a similar listing appears.",
    account: "Create an account to manage your alerts",
    mine: "See my alerts",
  },
};

export default function NotifyLikeThis({
  locale,
  filters,
  listingId,
}: {
  locale: Loc;
  /** Saved-alert filter blob (alert filter names, see lib/alert-filters). */
  filters: Record<string, unknown>;
  listingId: string;
}) {
  const c = COPY[locale] ?? COPY.az;
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // Push decides the promise: an iOS Safari tab cannot take a device
  // alert, so it is told the search comes filled in, not "no account".
  const [pushOk, setPushOk] = useState(true);
  useEffect(() => { setPushOk(isPushEligible()); }, []);

  const draft = { ...filters, min_verdict_tier: "any" };
  const formPath = `/alerts/new?prefill=${encodePrefill(draft)}`;

  function toForm(why: string) {
    trackEvent("listing.next.alert", { id: listingId, step: "form", why });
    window.location.href = `/${locale}${formPath}`;
  }

  async function onClick() {
    if (busy || done) return;
    trackEvent("listing.next.alert", { id: listingId, step: "tap", signed_in: user ? 1 : 0 });
    if (!isPushEligible()) return toForm("no_push");
    setBusy(true);
    try {
      const { sub, reason } = await getOrCreatePushSubscription("/follows/config");
      if (!sub) return toForm(reason ?? "no_sub");
      const r = await fetch(`${API_BASE}/alerts/anon`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filters, min_verdict_tier: "any", subscription: sub.toJSON() }),
      });
      const detail = r.ok ? null : (await r.json().catch(() => ({})))?.detail;
      if (r.ok || (detail && typeof detail === "object" && detail.reason === "duplicate")) {
        setDone(true);
        trackEvent("listing.next.alert", { id: listingId, step: "saved", via: "push" });
        return;
      }
      if (detail && typeof detail === "object" && detail.reason === "account_required") {
        // The one free device search is taken: the account is the next
        // step, and the form comes back filled after sign-in.
        trackEvent("listing.next.alert", { id: listingId, step: "account_required" });
        window.location.href = `/${locale}/auth/login?return_to=${encodeURIComponent(formPath)}`;
        return;
      }
      return toForm(typeof detail === "object" && detail?.reason ? String(detail.reason) : `http_${r.status}`);
    } catch {
      return toForm("error");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm" role="status">
        <p className="flex items-start gap-2 font-semibold text-gray-900">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
          <span>{c.done}</span>
        </p>
        <a
          href={user ? `/${locale}/alerts` : `/${locale}/auth/login?return_to=${encodeURIComponent("/alerts")}`}
          rel={user ? undefined : "nofollow"}
          data-track="listing.next.alert_account"
          data-track-props={JSON.stringify({ id: listingId, signed_in: user ? 1 : 0 })}
          className={`mt-1 inline-flex pl-6 text-sm ${LINK}`}
        >
          {user ? c.mine : c.account}
        </a>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className="flex w-full items-center gap-2.5 rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-left text-gray-900 transition-colors hover:border-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-60"
    >
      {busy
        ? <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" aria-hidden />
        : <Bell className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />}
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold">{c.cta}</span>
        <span className="block text-[11px] text-gray-500">{!pushOk ? c.subForm : user ? c.subIn : c.sub}</span>
      </span>
    </button>
  );
}
